import { Theme } from '@emotion/react';
import { Option } from 'rc-select';
import React from 'react';

import { useTranslations } from '~src/commons/utils/translations/translate';
import { fontStyles } from '~src/theme/fontStyles';

import { CategoryOption, SearchLocation } from '../../api/api';
import { Spinner } from '../commons/components/loader/spinner';
import { ListState } from '../ui/list-state';
import { SelectInput } from '../ui/select-input';
import { DistanceComponent } from './distance';
import { LocationAddress } from './location-address';
import { useLocationMutation, useLocationQuery } from './location-mutation';

interface LocationSearchProps {
  listState: ListState<CategoryOption>;
}

export const LocationSearch: React.FC<LocationSearchProps> = ({ listState }) => {
  const { tICU } = useTranslations();

  const [searchValue, setSearchValue] = React.useState<string>('');

  const query = useLocationQuery(searchValue);

  const mutation = useLocationMutation();

  // triggered when user clicks an option.
  const onSelectLocation = (locationId: string) => {
    setSearchValue(''); // reset search on selection.

    const existingOption = listState.items.find(option => option.location!.id === locationId);
    if (existingOption) {
      /**
       * 1. option already on the screen => just select it.
       */
      listState.setSelectedKey(locationId);
      return;
    }

    /**
     * 2. New location was selected => add it to session.
     */
    const newSearchLocation = query.data!.find(searchLocation => searchLocation.id === locationId)!;
    mutation.mutate(newSearchLocation);
    /**
     * Note: This option may not match any on the list until the state is
     * synced, make sure that we survive this.
     */
    listState.setSelectedKey(locationId);
  };

  function isOpen() {
    if (mutation.isPending) {
      // force open to display spinner.
      return true;
    }
    return undefined;
  }

  // Use the same spinner to show both loading state.
  const isLoading = query.isFetching || mutation.isPending;

  const children = (query.data || []).map(searchLocation => {
    return (
      <Option
        key={searchLocation.id}
        disabled={searchLocation.out_of_stock}
        title={searchLocation.name}
        value={searchLocation.id}
      >
        <LocationSearchOption searchLocation={searchLocation} />
      </Option>
    );
  });

  return (
    <SelectInput
      filterOption={false}
      notFoundContent={
        <LocationSearchPlaceholder isIdle={query.fetchStatus === 'idle'} isLoading={isLoading} />
      }
      placeholder={tICU('widget.INSTORE_SEARCH_PLACEHOLDER_SHORT')}
      open={isOpen()}
      showSearch={!mutation.isPending /* readonly when adding location */}
      searchValue={searchValue}
      // @ts-ignore
      value={null /* special option to force the input to show placeholder */}
      data-test="location-search"
      onSearch={setSearchValue}
      // @ts-ignore
      onSelect={onSelectLocation}
    >
      {isLoading ? undefined : children /* instruct rc-select to show placeholder when loading */}
    </SelectInput>
  );
};

interface LocationSearchOptionProps {
  searchLocation: SearchLocation;
}

const LocationSearchOption: React.FC<LocationSearchOptionProps> = ({ searchLocation }) => {
  const { tICU } = useTranslations();
  return (
    <div>
      <span css={styles.locationTitle}>{searchLocation.name}</span>
      <div css={styles.locationAddress}>
        <LocationAddress address={searchLocation.address} />
        <DistanceComponent distance={searchLocation.distance} showTimeOnly />
      </div>
      {searchLocation.out_of_stock && (
        <div css={styles.outOfStock}>{tICU('widget.INSTORE_SEARCH_OUT_OF_STOCK')}</div>
      )}
    </div>
  );
};

interface LocationSearchPlaceholderProps {
  isIdle: boolean;
  isLoading: boolean;
}

const LocationSearchPlaceholder: React.FC<LocationSearchPlaceholderProps> = ({
  isIdle,
  isLoading,
}) => {
  const { tICU } = useTranslations();
  /*
   * We use rc-select option-list placeholder to display both the spinner and
   * the actual placeholder.
   */
  if (isLoading) {
    // show spinner if loading or mutating.
    return <Spinner size="small" />;
  }

  if (isIdle) {
    /**
     * Option list will open on click (that's how rc-select works) but have
     * nothing to show -> display placeholder.
     */
    return <span>{tICU('widget.INSTORE_SEARCH_PLACEHOLDER')}</span>;
  }

  return <span>{tICU('widget.INSTORE_SEARCH_EMPTY_LOCATIONS_TITLE')}</span>;
};

const styles = {
  locationTitle: {
    fontVariationSettings: '"wght" 500',
    ...fontStyles.px14_20,
  },
  locationAddress: (theme: Theme) => ({
    fontSize: '12px',
    color: theme.neutral35,
    display: 'flex',
    justifyContent: 'space-between',
  }),
  outOfStock: (theme: Theme) => ({
    fontSize: '12px',
    color: theme.neutral35,
  }),
};
